import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import {
  Box,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { QuantityField } from 'components/Common/UnitEditor/QuantityField'
import { DeliveryTermsSelector } from 'features/DeliveryTerms/project/DeliveryTermsSelector'
import { PaymentTermsSelector } from 'features/PaymentTerms/project/PaymentTermsSelector'
import moment from 'moment'
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import {
  MoneyDto,
  QuantityDto,
  QuantityDtoType,
} from 'services/APIs/InternalAPI/internal-api.contracts'

const DEFAULT_QUOTATION_VALIDITY_DAYS = 30

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getToday(): Date {
  const today = new Date()
  return new Date(today.getFullYear(), today.getMonth(), today.getDate())
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

function getExpiryDateFromValidityDays(validityDays: number): string {
  return formatDateInputValue(addDays(getToday(), validityDays))
}

function getValidityDaysFromExpiryDate(expiryDate: string): number | undefined {
  if (!expiryDate) {
    return undefined
  }
  const parsedDate = new Date(`${expiryDate}T00:00:00`)
  if (Number.isNaN(parsedDate.getTime())) {
    return undefined
  }
  const millisecondsPerDay = 24 * 60 * 60 * 1000
  return Math.max(
    0,
    Math.round(
      (parsedDate.getTime() - getToday().getTime()) / millisecondsPerDay,
    ),
  )
}

export function getSynchronizedQuotationDates({
  validityDays,
  expiryDate,
}: {
  validityDays?: number | null
  expiryDate?: string | Date | null
}): {
  validityDays: number
  expiryDate: string
} {
  if (typeof validityDays === 'number' && validityDays >= 0) {
    return {
      validityDays,
      expiryDate: getExpiryDateFromValidityDays(validityDays),
    }
  }

  if (expiryDate) {
    const expiryDateString =
      expiryDate instanceof Date
        ? formatDateInputValue(expiryDate)
        : formatDateInputValue(new Date(expiryDate))
    const derivedValidityDays = getValidityDaysFromExpiryDate(expiryDateString)

    if (derivedValidityDays !== undefined) {
      return {
        validityDays: derivedValidityDays,
        expiryDate: expiryDateString,
      }
    }
  }

  return {
    validityDays: DEFAULT_QUOTATION_VALIDITY_DAYS,
    expiryDate: getExpiryDateFromValidityDays(DEFAULT_QUOTATION_VALIDITY_DAYS),
  }
}

export { DEFAULT_QUOTATION_VALIDITY_DAYS }

export type QuotationDetailsValues = {
  comment: string
  reference: string
  orderNumber: string
  deliveryTermsCode: string
  paymentTermsCode: string
  expireDate: string
  validityDays: number
  vatRatio: QuantityDto
}

export type QuotationDetailsRef = {
  getValues: () => QuotationDetailsValues
  setValues: (values: Partial<QuotationDetailsValues>) => void
}

type QuotationDetailsSectionProps = {
  disabled?: boolean
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
}

const detailFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'background.paper',
  },
}

const wideGridFieldSx = {
  gridColumn: {
    xs: '1 / -1',
    sm: 'span 6',
    md: 'span 5',
  },
}

const narrowGridFieldSx = {
  gridColumn: {
    xs: '1 / -1',
    sm: 'span 4',
    md: 'span 2',
  },
}

const termsGridFieldSx = {
  gridColumn: {
    xs: '1 / -1',
    sm: 'span 6',
    md: 'span 3',
  },
  '&:empty': {
    display: 'none',
  },
}

function createDefaultVatRatio(): QuantityDto {
  return {
    value: 21,
    unit: '%',
    quantityType: QuantityDtoType.Ratio,
  }
}

export const QuotationDetailsSection = forwardRef<
  QuotationDetailsRef,
  QuotationDetailsSectionProps
>(function QuotationDetailsSection(
  { disabled, expanded, onExpandedChange },
  ref,
) {
  const [orderNumber, setOrderNumber] = useState('')
  const [reference, setReference] = useState('')
  const [deliveryTermsCode, setDeliveryTermsCode] = useState('')
  const [paymentTermsCode, setPaymentTermsCode] = useState('')
  const [vatRatio, setVatRatio] = useState<QuantityDto>(createDefaultVatRatio())
  const [expireDate, setExpireDate] = useState(() =>
    getExpiryDateFromValidityDays(DEFAULT_QUOTATION_VALIDITY_DAYS),
  )
  const [validityDays, setValidityDays] = useState<number>(0)
  const [comment, setComment] = useState('')
  const expireMoment = useMemo(
    () => (expireDate ? moment(expireDate) : null),
    [expireDate],
  )

  useImperativeHandle(
    ref,
    () => ({
      getValues: () => ({
        comment,
        reference,
        orderNumber,
        deliveryTermsCode,
        paymentTermsCode,
        expireDate,
        validityDays,
        vatRatio,
      }),
      setValues: (values: Partial<QuotationDetailsValues>) => {
        if (values.comment !== undefined) setComment(values.comment)
        if (values.reference !== undefined) setReference(values.reference)
        if (values.orderNumber !== undefined) setOrderNumber(values.orderNumber)
        if (values.deliveryTermsCode !== undefined)
          setDeliveryTermsCode(values.deliveryTermsCode)
        if (values.paymentTermsCode !== undefined)
          setPaymentTermsCode(values.paymentTermsCode)
        if (values.expireDate !== undefined) setExpireDate(values.expireDate)
        if (values.validityDays !== undefined)
          setValidityDays(values.validityDays)
        if (values.vatRatio !== undefined) setVatRatio(values.vatRatio)
      },
    }),
    [
      comment,
      reference,
      orderNumber,
      deliveryTermsCode,
      paymentTermsCode,
      expireDate,
      validityDays,
      vatRatio,
    ],
  )

  const handleDateChange = useCallback((date: moment.Moment | null) => {
    const nextExpireDate = date ? formatDateInputValue(date.toDate()) : ''
    const nextValidityDays = getValidityDaysFromExpiryDate(nextExpireDate)

    setExpireDate(nextExpireDate)
    if (nextValidityDays !== undefined) {
      setValidityDays(nextValidityDays)
    }
  }, [])

  const handleValidityDaysChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseInt(e.target.value, 10)

      if (Number(isNaN(val))) return

      const sanitizedValidityDays = Math.max(0, val)
      setValidityDays(sanitizedValidityDays)
      setExpireDate(getExpiryDateFromValidityDays(sanitizedValidityDays))
    },
    [],
  )

  return (
    <Box sx={{ px: 3, pb: 2 }}>
      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          boxShadow: (theme) => theme.shadows[1],
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            px: 2,
            py: 1.5,
            backgroundColor: 'background.paper',
          }}
          onClick={() => onExpandedChange(!expanded)}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Quotation Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Header fields sent with quotation response
            </Typography>
          </Box>
          <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </Box>
        <Collapse in={expanded}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
              gap: 1.5,
              p: 2,
              '&:not(:has(.quotation-terms-field:not(:empty))) .quotation-comment-field':
                {
                  gridColumn: {
                    md: 'span 6',
                  },
                },
            }}
          >
            <TextField
              size="small"
              variant="standard"
              label="Order Number"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              disabled={disabled}
              sx={{ ...detailFieldSx, ...wideGridFieldSx }}
            />
            <TextField
              size="small"
              variant="standard"
              label="Reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              disabled={disabled}
              sx={{ ...detailFieldSx, ...wideGridFieldSx }}
            />
            <QuantityField
              size="small"
              fullWidth
              variant="standard"
              label="VAT"
              name="vatRatio"
              value={vatRatio}
              decimalScale={3}
              fixedDecimalScale={false}
              onChange={(value: QuantityDto | MoneyDto) =>
                setVatRatio(value as QuantityDto)
              }
              disabled={disabled}
              sx={{
                ...narrowGridFieldSx,
                alignSelf: 'end',
              }}
            />
            <Box className="quotation-terms-field" sx={termsGridFieldSx}>
              <DeliveryTermsSelector
                label="delivery terms"
                size="medium"
                placeholder="select the delivery term"
                labelProps={{
                  sx: {
                    fontWeight: 400,
                  },
                }}
                onCleanDeliveryTerms={() => setDeliveryTermsCode(null)}
                selectedDeliveryTermCode={deliveryTermsCode}
                onDeliveryTermSelected={(code) => setDeliveryTermsCode(code)}
                disabled={disabled}
              />
            </Box>
            <Box className="quotation-terms-field" sx={termsGridFieldSx}>
              <PaymentTermsSelector
                label="payment terms"
                size="medium"
                placeholder="select the payment term"
                onCleaned={() => setPaymentTermsCode(null)}
                selectedPaymentTermCode={paymentTermsCode}
                onPaymentTermSelected={(code) => setPaymentTermsCode(code)}
                disabled={disabled}
              />
            </Box>
            <DatePicker
              label="Expiry Date"
              value={expireMoment}
              disablePast
              onChange={handleDateChange}
              disabled={disabled}
              slotProps={{
                textField: { size: 'medium', variant: 'standard' },
              }}
              sx={{
                ...detailFieldSx,
                gridColumn: {
                  xs: '1 / -1',
                  sm: 'span 6',
                  md: 'span 4',
                },
              }}
            />
            <TextField
              variant="standard"
              label="Validity (days)"
              type="number"
              value={validityDays}
              className={'input-quantity-field'}
              onFocus={(e) => e.target.select()}
              onChange={handleValidityDaysChange}
              disabled={disabled}
              slotProps={{
                input: {
                  className: 'input-quantity',
                },
                htmlInput: { min: 0 },
              }}
              sx={{ ...detailFieldSx, ...narrowGridFieldSx }}
            />
            <TextField
              className="quotation-comment-field"
              variant="standard"
              label="Comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={disabled}
              multiline
              minRows={1}
              maxRows={4}
              slotProps={{
                htmlInput: { maxLength: 150 },
              }}
              sx={{ ...detailFieldSx, gridColumn: '1 / -1' }}
              placeholder="Additional notes for the buyer..."
            />
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
})
